import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { AxiosError } from 'axios';
import useAuthGuard from '../../hooks/useAuthGuard';
import api from '../../services/api';
import { TextInput } from '../../components/FormComponents/TextInput';
import { Select } from '../../components/FormComponents/Select';

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters').optional(),
  role: z.enum(['admin', 'super_admin'] as const),
});

type FormData = z.infer<typeof schema>;

const AdminFormPage = () => {
  useAuthGuard(['super_admin']);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      role: 'admin',
    },
  });

  const loadAdmin = useCallback(async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/admins/${id}`);
      // Handle both response structures: { success: true, data: admin } or { success: true, data: { ...admin } }
      const adminData = response.data.data || response.data;
      setValue('name', adminData.name);
      setValue('email', adminData.email);
      setValue('role', adminData.role);
    } catch (loadError) {
      console.error('Failed to load admin:', loadError);
      setError('Failed to load admin details');
    } finally {
      setLoading(false);
    }
  }, [id, setValue]);

  useEffect(() => {
    if (isEdit) {
      loadAdmin();
    }
  }, [isEdit, loadAdmin]);

  const onSubmit = async (data: FormData) => {
    try {
      setLoading(true);
      setError('');

      if (isEdit) {
        await api.patch(`/admins/${id}`, data);
      } else {
        await api.post('/admins', data);
      }

      navigate('/admins');
    } catch (submitError) {
      console.error('Failed to save admin:', submitError);
      const axiosError = submitError as AxiosError<{ message?: string }>;
      const message = axiosError.response?.data?.message ?? 'Failed to save admin. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4 }}>
        {isEdit ? 'Edit Admin' : 'Create New Admin'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextInput
                  name="name"
                  control={control}
                  label="Name"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <TextInput
                  name="email"
                  control={control}
                  label="Email"
                  type="email"
                  fullWidth
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              </Grid>

              {!isEdit && (
                <Grid item xs={12}>
                  <TextInput
                    name="password"
                    control={control}
                    type="password"
                    label="Password"
                    fullWidth
                    error={!!errors.password}
                    helperText={errors.password?.message}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Select
                  name="role"
                  control={control}
                  label="Role"
                  fullWidth
                  error={!!errors.role}
                  helperText={errors.role?.message}
                  options={[
                    { value: 'admin', label: 'Admin' },
                    { value: 'super_admin', label: 'Super Admin' },
                  ]}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/admins')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminFormPage;